/**
 * ============================================================================
 * MASTER PAGE: Roles & Permissions Management (`tbl_roles`, `tbl_role_operations`)
 * ============================================================================
 * Purpose:
 *   Simple, clear, and intuitive admin interface for managing system roles,
 *   custom tenant roles, hierarchy levels, and granular API action permissions.
 *
 * Database Scope:
 *   Master Database (`digimaster`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import { roleService, operationService } from "../../services";

// SVG Icons
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);

const KeyIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
  </svg>
);

const ShieldCheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
  </svg>
);

const LockClosedIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

const FunnelIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

export const isSystemRole = () => false;

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, totalPages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "inactive"

  // Role Add / Edit Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    str_role_name: "",
    int_role_hirarchy: 50,
    bool_is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState(null);

  // Permissions Modal State
  const [isPermModalOpen, setIsPermModalOpen] = useState(false);
  const [permRole, setPermRole] = useState(null);
  const [allOperations, setAllOperations] = useState([]);
  const [selectedOpIds, setSelectedOpIds] = useState([]);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);
  const [permSearch, setPermSearch] = useState("");

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await roleService.get({
        page: pagination?.page || 1,
        limit: pagination?.limit || 10,
        search,
        sortBy: "int_role_hirarchy",
        sortOrder: "desc",
      });
      if (res && res.success) {
        setRoles(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        } else if (res.meta) {
          setPagination(res.meta);
        }
      }
    } catch (err) {
      console.error("Failed to load roles:", err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination?.page, pagination?.limit, search]);

  const loadOperations = useCallback(async () => {
    try {
      const res = await operationService.get({ limit: 100 });
      if (res && res.success) {
        setAllOperations(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load operations:", err.message);
    }
  }, []);

  useEffect(() => {
    loadRoles();
    loadOperations();
  }, [loadRoles, loadOperations]);

  // Track used hierarchy levels to prevent duplicate key collisions
  const usedHierarchyMap = useMemo(() => {
    const map = new Map();
    roles.forEach((r) => {
      if (!selectedRole || r._id !== selectedRole._id) {
        const level = Number(r.int_role_hirarchy ?? r.int_role_hierarchy);
        map.set(level, r.str_role_name);
      }
    });
    return map;
  }, [roles, selectedRole]);

  const levelConflictRole = usedHierarchyMap.get(Number(formData.int_role_hirarchy));

  const getNextAvailableLevel = () => {
    let candidate = 50;
    while (candidate >= 1 && usedHierarchyMap.has(candidate)) {
      candidate -= 5;
    }
    if (candidate < 1) {
      candidate = 60;
      while (candidate <= 99 && usedHierarchyMap.has(candidate)) {
        candidate += 1;
      }
    }
    return Math.max(1, Math.min(99, candidate));
  };

  const handleAddNew = () => {
    setSelectedRole(null);
    setFormData({
      str_role_name: "",
      int_role_hirarchy: getNextAvailableLevel(),
      bool_is_active: true,
    });
    setIsFormOpen(true);
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setFormData({
      str_role_name: role.str_role_name || "",
      int_role_hirarchy: Number(role.int_role_hirarchy ?? role.int_role_hierarchy ?? 50),
      bool_is_active: role.bool_is_active ?? true,
    });
    setIsFormOpen(true);
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    const roleName = formData.str_role_name.trim();
    if (!roleName) {
      Swal.fire({
        title: "Role Name Required",
        text: "Please enter a role name.",
        icon: "warning",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    const hierarchyNum = Number(formData.int_role_hirarchy ?? formData.int_role_hierarchy);
    if (isNaN(hierarchyNum) || hierarchyNum < 1 || hierarchyNum > 99) {
      Swal.fire({
        title: "Invalid Hierarchy Level",
        text: "Hierarchy Level must be a valid number between 1 and 99.",
        icon: "warning",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    if (levelConflictRole) {
      Swal.fire({
        title: "Hierarchy Level Conflict",
        text: `Hierarchy Level ${hierarchyNum} is already assigned to "${levelConflictRole}". Please choose a different number.`,
        icon: "warning",
        confirmButtonColor: "#0f172a",
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        str_role_name: roleName,
        int_role_hierarchy: hierarchyNum,
        int_role_hirarchy: hierarchyNum,
        bool_is_active: Boolean(formData.bool_is_active),
      };

      if (selectedRole) {
        await roleService.edit(selectedRole._id, payload);
        await Swal.fire({
          title: "Role Updated!",
          text: `Role "${roleName}" has been updated successfully.`,
          icon: "success",
          confirmButtonColor: "#0f172a",
        });
      } else {
        await roleService.create(payload);
        await Swal.fire({
          title: "Role Created!",
          text: `New role "${roleName}" created successfully.`,
          icon: "success",
          confirmButtonColor: "#0f172a",
        });
      }
      setIsFormOpen(false);
      loadRoles();
    } catch (err) {
      Swal.fire({
        title: "Failed to Save Role",
        text: err.message || "An error occurred while saving the role.",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRole = async (role) => {
    const confirmRes = await Swal.fire({
      title: "Delete Role?",
      html: `Are you sure you want to delete role <b>${role.str_role_name}</b>?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!confirmRes.isConfirmed) return;

    try {
      await roleService.delete(role._id);
      await Swal.fire({
        title: "Role Deleted!",
        text: `Role "${role.str_role_name}" has been deleted.`,
        icon: "success",
        confirmButtonColor: "#0f172a",
      });
      loadRoles();
    } catch (err) {
      Swal.fire({
        title: "Delete Failed",
        text: err.message || "Failed to delete role",
        icon: "error",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleOpenPermissions = async (role) => {
    setPermRole(role);
    setIsPermModalOpen(true);
    setLoadingPerms(true);
    setPermSearch("");
    try {
      const res = await roleService.getRolePermissions(role.int_role_id);
      if (res.success && Array.isArray(res.data)) {
        setSelectedOpIds(res.data.map((item) => Number(item.int_operation_id)));
      } else {
        setSelectedOpIds([]);
      }
    } catch (err) {
      console.error("Failed to load permissions:", err);
      setSelectedOpIds([]);
    } finally {
      setLoadingPerms(false);
    }
  };

  const handleTogglePermission = (opId) => {
    const id = Number(opId);
    setSelectedOpIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleToggleAllPermissions = () => {
    if (selectedOpIds.length === allOperations.length) {
      setSelectedOpIds([]);
    } else {
      setSelectedOpIds(allOperations.map((op) => Number(op.int_operation_id)));
    }
  };

  const handleSavePermissions = async () => {
    if (!permRole) return;
    setSavingPerms(true);
    try {
      await roleService.assignPermissions(permRole.int_role_id, selectedOpIds);
      setFeedbackMsg({
        type: "success",
        text: `Permissions updated for "${permRole.str_role_name}" (${selectedOpIds.length} operations granted).`,
      });
      setIsPermModalOpen(false);
      setTimeout(() => setFeedbackMsg(null), 4000);
    } catch (err) {
      alert(err.message || "Failed to update permissions");
    } finally {
      setSavingPerms(false);
    }
  };

  const filteredRoles = useMemo(() => {
    return roles.filter((role) => {
      if (statusFilter === "active") return role.bool_is_active !== false;
      if (statusFilter === "inactive") return role.bool_is_active === false;
      return true;
    });
  }, [roles, statusFilter]);

  const totalRoles = pagination.total || roles.length;
  const activeCount = roles.filter((r) => r.bool_is_active !== false).length;
  const inactiveCount = roles.filter((r) => r.bool_is_active === false).length;
  const totalPages = pagination.totalPages || Math.ceil(totalRoles / pagination.limit) || 1;

  const filteredOperations = useMemo(() => {
    if (!permSearch.trim()) return allOperations;
    const q = permSearch.toLowerCase();
    return allOperations.filter(
      (op) =>
        (op.str_operation_name || "").toLowerCase().includes(q) ||
        (op.str_url_name || "").toLowerCase().includes(q)
    );
  }, [allOperations, permSearch]);

  return (
    <div className="space-y-6 mx-auto pb-12">
      {/* Toast Feedback */}
      {feedbackMsg && (
        <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 text-xs font-semibold flex items-center gap-2 shadow-sm animate-fade-in">
          <CheckCircleIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
          <span>{feedbackMsg.text}</span>
        </div>
      )}

      {/* Header section with Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-blue-600 bg-clip-text text-transparent mb-2">
              Roles & Permissions
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Define administrative hierarchy tiers, create custom tenant roles, and assign granular operation access
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={loadRoles}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-sm shadow-blue-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={handleAddNew}
              className="inline-flex items-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-sm shadow-green-500/20 text-xs sm:text-sm cursor-pointer"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Custom Role
            </button>
          </div>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
            {totalRoles}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Total Access Roles
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-emerald-600 font-mono">
            {activeCount}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Active Roles
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-slate-500 font-mono">
            {inactiveCount}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Inactive Roles
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-2xl font-bold text-green-600 font-mono">
            {allOperations.length}
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Granular API Operations
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4 items-stretch sm:items-center">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search role name or role ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          <div className="relative">
            <FunnelIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-9 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All Roles</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <div className="relative">
            <select
              value={pagination.limit}
              onChange={(e) => {
                setPagination((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }));
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs sm:text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Roles Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <ArrowPathIcon className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading roles & permissions...</p>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="py-16 px-4 text-center">
            <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
              <ShieldCheckIcon className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No Roles Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto text-sm">
              {search || statusFilter !== "all"
                ? "No roles match your search filter."
                : "No roles registered in the master database."}
            </p>
            <button
              onClick={handleAddNew}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-200 text-sm cursor-pointer"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Custom Role
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/60">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider w-14">
                      #
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Role Name
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Role ID
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Hierarchy Tier
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRoles.map((row, index) => {
                    const rowNumber = (pagination.page - 1) * pagination.limit + index + 1;
                    const isSys = isSystemRole(row);
                    const hierarchyLevel = row.int_role_hirarchy ?? row.int_role_hierarchy ?? 0;

                    return (
                      <tr
                        key={row._id || index}
                        className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-400">
                          {rowNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center font-bold bg-blue-500/10 text-blue-600">
                              <ShieldCheckIcon className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                                {row.str_role_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-xs px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold">
                            #{row.int_role_id ?? row._id?.slice(-4) ?? "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600">
                            Tier {hierarchyLevel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            row.bool_is_active !== false
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {row.bool_is_active !== false ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleOpenPermissions(row)}
                              className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors cursor-pointer"
                              title="Assign API Permissions"
                            >
                              <KeyIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(row)}
                              className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors cursor-pointer"
                              title="Edit Role"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteRole(row)}
                              className="p-1.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer"
                              title="Delete Role"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {totalRoles === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {Math.min(pagination.page * pagination.limit, totalRoles)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900 dark:text-white">{totalRoles}</span> results
              </div>
              <div className="flex items-center space-x-1.5">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNumber = i + 1;
                  } else if (pagination.page >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setPagination((prev) => ({ ...prev, page: pageNumber }))}
                      className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                        pagination.page === pageNumber
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm"
                          : "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                  disabled={pagination.page === totalPages || totalPages === 0}
                  className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add / Edit Role Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 transition-all">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 mb-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedRole ? "Edit Role" : "Create Custom Role"}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure role hierarchy level and status
                </p>
              </div>
              <button
                onClick={() => setIsFormOpen(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveRole} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Role Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Finance Officer, Support Lead"
                  value={formData.str_role_name}
                  onChange={(e) => setFormData({ ...formData, str_role_name: e.target.value })}
                  className="w-full px-3.5 py-2.5 text-xs sm:text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Hierarchy Level (1-99) *
                </label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  required
                  value={formData.int_role_hirarchy}
                  onChange={(e) => setFormData({ ...formData, int_role_hirarchy: Number(e.target.value) })}
                  className="w-full px-3.5 py-2.5 text-xs sm:text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                />
                {levelConflictRole && (
                  <p className="text-xs text-rose-500 mt-1">
                    Level {formData.int_role_hirarchy} is already assigned to "{levelConflictRole}".
                  </p>
                )}
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="bool_is_active"
                  checked={formData.bool_is_active}
                  onChange={(e) => setFormData({ ...formData, bool_is_active: e.target.checked })}
                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                />
                <label htmlFor="bool_is_active" className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  Role is Active
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 mt-6">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white shadow-sm shadow-green-500/20 transition-all cursor-pointer disabled:opacity-50"
                >
                  {submitting ? "Saving..." : selectedRole ? "Update Role" : "Save Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Permissions Modal */}
      {isPermModalOpen && permRole && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 transition-all max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Assign Operations: {permRole.str_role_name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Select granular API endpoints and UI operations permitted for this role
                </p>
              </div>
              <button
                onClick={() => setIsPermModalOpen(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Permissions Search & Quick Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter permissions..."
                  value={permSearch}
                  onChange={(e) => setPermSearch(e.target.value)}
                  className="pl-9 pr-3 py-1.5 w-full text-xs rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={handleToggleAllPermissions}
                className="px-3 py-1.5 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                {selectedOpIds.length === allOperations.length ? "Deselect All" : "Select All"} ({selectedOpIds.length}/{allOperations.length})
              </button>
            </div>

            {/* Operations List */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-2 border border-gray-200 dark:border-gray-700 rounded-xl p-3 bg-gray-50/50 dark:bg-gray-750">
              {loadingPerms ? (
                <div className="py-12 text-center">
                  <ArrowPathIcon className="w-6 h-6 mx-auto text-blue-500 animate-spin mb-2" />
                  <p className="text-xs text-gray-400">Loading operations catalogue...</p>
                </div>
              ) : filteredOperations.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-8">No operations match your search query.</p>
              ) : (
                filteredOperations.map((op) => {
                  const opId = Number(op.int_operation_id);
                  const isChecked = selectedOpIds.includes(opId);

                  return (
                    <label
                      key={opId}
                      className={`flex items-start space-x-3 p-2.5 rounded-xl border transition-all cursor-pointer ${
                        isChecked
                          ? "bg-green-50/70 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleTogglePermission(opId)}
                        className="mt-0.5 w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                      />
                      <div className="flex-1">
                        <div className="text-xs font-semibold text-gray-900 dark:text-white">
                          {op.str_operation_name || `Operation #${opId}`}
                        </div>
                        {op.str_url_name && (
                          <div className="text-[11px] font-mono text-gray-400 mt-0.5">
                            {op.str_url_name}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 mt-4">
              <button
                type="button"
                onClick={() => setIsPermModalOpen(false)}
                className="px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePermissions}
                disabled={savingPerms}
                className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white shadow-sm shadow-green-500/20 transition-all cursor-pointer disabled:opacity-50"
              >
                {savingPerms ? "Saving Permissions..." : `Save Permissions (${selectedOpIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
